import React from 'react';
import {
    Box, Modal, Fade, Grid, TextField, Button, IconButton,
} from '@mui/material';
import styled from '@emotion/styled';
import swal from 'sweetalert2';
import CloseIcon from '@mui/icons-material/Close';
import { CreateInputDataSaveMutationVariables } from 'API';
import { fetchAuthSession } from 'aws-amplify/auth';
import { generateClient, GraphQLResult } from 'aws-amplify/api';
import { createInputDataSave } from 'graphql/mutations';
import { inputDataSavesByMailAddressAndConditionsName } from 'graphql/queries';
import { useNavigate } from 'react-router-dom';
import { sessionDisconnectedCheck } from 'shared/sessionDisconnectedCheck';
import { SystemErrorTitle, SystemErrorMsg } from '../../constants';
import { BackdropLoading } from 'shared/backdropLoading';

interface OwnProps {
    isOpen: boolean,
    financialAnalysisData: { [key: string]: any },
    yearEdition: any,
    handleClose: (isOpen: boolean) => void
}

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
    height: '42%',
    bgcolor: 'background.paper',
    textAlign: 'center',
    borderRadius: '15px',
};

const ModalLabel = styled(Box)(({ }) => ({
    backgroundColor: '#41DBCE',
    fontSize: '1.2rem',
    color: '#FFFFFF',
    height: '2rem',
    paddingTop: '0.5rem',
    borderRadius: '15px 15px 0 0',
    textAlign: 'left',
    paddingLeft: '1rem'
}));

const IndustryArea = styled(Box)(({ }) => ({
    backgroundColor: '#F2F2F2',
    textAlign: 'center',
    fontSize: '1.5rem',
    height: '6rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '1rem',
}));

const TextFieldBox = styled(Box)(({ }) => ({
    textAlign: 'center',
    fontSize: '1.2rem',
    color: '#595959',
    width: '90%',
}));

const TextFieldLabel = styled(Grid)(({ }) => ({
    backgroundColor: '#595959',
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}));

const TextFieldForm = styled(Grid)(({ }) => ({
    backgroundColor: '#FFFFFF',
}));

const DataInputForm = styled(TextField)(({ }) => ({
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            border: 'none',
        },
        '&:hover fieldset': {
            border: 'none',
        },
        '&.Mui-focused fieldset': {
            border: 'none',
        },
    },
}))

const SaveButton = styled(Button)(({ }) => ({
    backgroundColor: '#01CFBE',
    color: '#FFFFFF',
    textAlign: 'center',
    fontSize: '1rem',
    fontWeight: 'bold',
    height: '2.3rem',
    width: '12rem',
    borderRadius: '100rem',
    marginBottom: '1rem'
}));

const screenId = "U041"

const SaveConditionModal = (props: OwnProps) => {
    const navigate = useNavigate();
    const { isOpen, financialAnalysisData, yearEdition, handleClose } = props
    const [open, setOpen] = React.useState(isOpen);
    const [coditionsName, setCoditionsName] = React.useState("");
    const [loading, setLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        setOpen(isOpen)
        if (!isOpen) {
            setCoditionsName("")
        }
    }, [isOpen])

    const getDataListCount = async () => {
        const session = await fetchAuthSession();
        let filter = {
            and: [
                { _deleted: { ne: true } }
            ]
        };
        let allItems: any[] = [];
        let nextToken: string | null = null;
        try {
            do {
                const client = generateClient();
                const result: GraphQLResult<any> = await client.graphql({
                    query: inputDataSavesByMailAddressAndConditionsName,
                    variables: {
                        mailAddress: session?.tokens?.idToken?.payload.email?.toString() || "",
                        filter: filter,
                        nextToken: nextToken,
                        limit: 500
                    }
                });
                const items = result.data?.inputDataSavesByMailAddressAndConditionsName?.items || [];
                nextToken = result.data?.inputDataSavesByMailAddressAndConditionsName?.nextToken || null;
                allItems = allItems.concat(items)
            } while (nextToken);
        } catch (error) {
            navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
        }
        return allItems.length || 0;
    };

    const handleSave = async () => {
        // 保存名チェック
        let numberOfSavedConditions = await getDataListCount()
        if (coditionsName == '') {
            swal.fire({
                text: '保存名を入力してください',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } else if (coditionsName.length > 60) {
            swal.fire({
                text: '保存名は60文字以内で入力してください',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } else if (numberOfSavedConditions >= 20) {
            swal.fire({
                html: '登録可能な入力データは20件までです<br>保存データ読込ボタンより、<br>不要な入力データの削除をお願いします',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } else {
            setLoading(true)
            // セッションの有効性確認
            if (await sessionDisconnectedCheck(screenId, "handleSave", yearEdition, navigate)) {
                return
            }
            const createNewInputDataSave = async () => {
                const session = await fetchAuthSession();
                const input: CreateInputDataSaveMutationVariables = {
                    input: {
                        mailAddress: session?.tokens?.idToken?.payload.email?.toString(),
                        userId: session?.tokens?.idToken?.payload.sub,
                        conditionsName: coditionsName,
                        financialAnalysisData: JSON.stringify(financialAnalysisData),
                    },
                };
                try {
                    const client = generateClient({ authMode: 'userPool' });
                    await client.graphql({
                        query: createInputDataSave,
                        variables: input,
                    });
                    setLoading(false)
                    swal.fire({
                        text: '入力データの保存が完了しました',
                        icon: 'success',
                        confirmButtonText: 'OK'
                    }).then((result) => {
                        if (result.isConfirmed) {
                            setCoditionsName('')
                            handleClose(false)
                        }
                    });
                } catch (error) {
                    navigate("/Error", { state: { title: SystemErrorTitle, message: SystemErrorMsg } });
                }
            };
            createNewInputDataSave()
        }
    }

    const handleChange = (v: string) => {
        setCoditionsName(v)
    }

    return (
        <>
            <BackdropLoading open={loading} />
            <Modal
                open={open}
                onClose={handleClose}
                closeAfterTransition
                sx={{
                    zIndex: 900
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            zIndex: 900
                        }
                    }
                }}
            >
                <Fade in={open} style={{ zIndex: 1000 }}>
                    <Box sx={modalStyle} tabIndex={-1}>
                        <ModalLabel>
                            入力データ保存
                            <IconButton onClick={() => handleClose(false)} sx={{ position: 'absolute', top: '1px', right: '1rem' }}>
                                <CloseIcon style={{ color: '#fff' }} />
                            </IconButton>
                        </ModalLabel>
                        <IndustryArea>
                            <TextFieldBox border={1}>
                                <Grid container>
                                    <TextFieldLabel size={2}>保存名</TextFieldLabel>
                                    <TextFieldForm size={10}>
                                        <DataInputForm placeholder="データ保存名を入力" fullWidth variant='outlined' size='small' onChange={(e) => handleChange(e.target.value)} />
                                    </TextFieldForm>
                                </Grid>
                            </TextFieldBox>
                        </IndustryArea>
                        <div>
                            <SaveButton variant="contained" color="secondary" onClick={() => handleSave()}>保存</SaveButton>
                        </div>
                        <div>
                            <span onClick={() => handleClose(false)} style={{ cursor: 'pointer' }}>
                                <img src={`${process.env.PUBLIC_URL}/Icons/returnIcon.svg`} width="13px" />&nbsp;&nbsp;戻る
                            </span>
                        </div>
                    </Box>
                </Fade>
            </Modal>
        </>
    );
}

export default SaveConditionModal;
